import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getEntradaService,
    crearEntradaService,
    updateEntradaService,
    getTiposPagoService,
    eliminarEntradaService,
    getMediasService,
    updateMediasService
} from '../services/EntradaService';
import { entradaCreateSchema } from '../utils/validation';
import FieldError from '../utils/FieldError';

const EntradaManager = () => {
    const [entradas, setEntradas] = useState([]);
    const [tiposPago, setTiposPago] = useState([]);
    const [media, setMedia] = useState(null);
    const [precioMedia, setPrecioMedia] = useState('');
    const [mensaje, setMensaje] = useState('');
    const [editandoId, setEditandoId] = useState(null);
    const [errors, setErrors] = useState({});

    const [nombreEntrada, setNombreEntrada] = useState('');
    const [tipoPago, setTipoPago] = useState('');
    const [precio, setPrecio] = useState('');
    const [duracion, setDuracion] = useState('');
    const [cantidadEntradas, setCantidadEntradas] = useState('');
    const [showForm, setShowForm] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        cargarEntrada();
        cargarTiposPago();
        cargarMedia();
    }, []);

    const cargarEntrada = async () => {
        try {
            const data = await getEntradaService();
            setEntradas(data);
        } catch (error) {
            setMensaje(error.mensaje || 'Error al cargar entradas');
        }
    };

    const cargarTiposPago = async () => {
        try {
            const data = await getTiposPagoService();
            setTiposPago(data);
        } catch (error) {
            setMensaje(error.mensaje || 'Error al cargar tipos de pago');
        }
    };

    const cargarMedia = async () => {
        try {
            const data = await getMediasService();
            setMedia(data);
            setPrecioMedia(data?.precioUnitario || '');
        } catch (error) {
            setMedia(null);
            setPrecioMedia('');
            setMensaje(error.mensaje || 'No hay información de medias');
        }
    };

    const handleGuardarMedia = async () => {
        try {
            const updated = await updateMediasService({
                id: media?.id || null,
                precioUnitario: parseFloat(precioMedia)
            });
            setMedia(updated);
            setPrecioMedia(updated.precioUnitario);
            setMensaje('Media guardada correctamente.');
        } catch (error) {
            setMensaje(error.mensaje || 'Error al guardar media');
        }
    };

    const handleCrear = async () => {
        const payload = { nombreEntrada, tipoPago, precio: parseFloat(precio), duracion: parseFloat(duracion), cantidadEntradas: parseInt(cantidadEntradas) };
        const result = entradaCreateSchema.safeParse(payload);
        if (!result.success) {
            const fieldErrors = {};
            result.error.issues.forEach((i) => { fieldErrors[i.path[0]] = i.message; });
            setErrors(fieldErrors);
            return;
        }
        try {
            const nuevaEntrada = await crearEntradaService(result.data);
            setEntradas(prev => [...prev, nuevaEntrada]);
            setMensaje('Entrada creada correctamente.');
            limpiarFormulario();
            setShowForm(false);
        } catch (error) {
            setMensaje(error.mensaje || 'Error al crear entrada');
        }
    };

    const handleUpdateEntrada = async (entrada) => {
        try {
            await updateEntradaService({
                id: entrada.id,
                nombreEntrada: entrada.nombreEntrada,
                tipoPago: entrada.tipoPago,
                precio: parseFloat(entrada.precio),
                duracion: parseFloat(entrada.duracion),
                cantidadEntradas: parseInt(entrada.cantidadEntradas)
            });
            setMensaje(`Entrada "${entrada.nombreEntrada}" actualizada correctamente.`);
            setEditandoId(null);
            cargarEntrada();
        } catch (err) {
            setMensaje(err.mensaje || 'Error al actualizar entrada');
        }
    };

    const handleEliminarEntrada = async (entradaId) => {
        if (!window.confirm("¿Estás seguro que quieres eliminar esta entrada?")) return;
        try {
            await eliminarEntradaService(entradaId);
            setEntradas(prev => prev.filter(e => e.id !== entradaId));
            setMensaje("Entrada eliminada correctamente.");
        } catch (error) {
            setMensaje(error.mensaje || "Error al eliminar entrada");
        }
    };

    const limpiarFormulario = () => {
        setNombreEntrada('');
        setTipoPago('');
        setPrecio('');
        setDuracion('');
        setCantidadEntradas('');
        setErrors({});
    };

    const onVolver = () => {
        navigate(-1);
    };

    return (
        <div className="max-w-4xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-6">
                <h2 className="text-xl font-semibold text-gray-900 text-center">Gestión de Entradas</h2>

                {mensaje && (
                    <p className="text-sm text-green-600 bg-green-50 border border-green-200 rounded-md px-4 py-2">{mensaje}</p>
                )}

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">Medias</h3>
                    <div className="flex items-end gap-3">
                        <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-500 mb-1">Precio Unitario:</label>
                            <input
                                type="number"
                                value={precioMedia}
                                onChange={e => setPrecioMedia(e.target.value)}
                                placeholder="Ingrese precio"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                        </div>
                        <button onClick={handleGuardarMedia} className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors whitespace-nowrap">
                            {media ? 'Actualizar Media' : 'Crear Media'}
                        </button>
                    </div>
                </div>

                <button
                    className="w-full py-2.5 px-4 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors"
                    onClick={() => setShowForm(prev => !prev)}
                >
                    {showForm ? 'Ocultar Formulario de Creación' : 'Crear Nueva Entrada'}
                </button>

                {showForm && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                        <h3 className="text-sm font-semibold text-gray-700">Crear Entrada</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label className="block text-xs font-medium text-gray-500">Nombre:</label>
                                <input value={nombreEntrada} onChange={e => { setNombreEntrada(e.target.value); if (errors.nombreEntrada) setErrors((p) => ({ ...p, nombreEntrada: undefined })); }} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                                <FieldError message={errors.nombreEntrada} />
                            </div>
                            <div className="space-y-1">
                                <label className="block text-xs font-medium text-gray-500">Tipo de Pago:</label>
                                <select value={tipoPago} onChange={e => { setTipoPago(e.target.value); if (errors.tipoPago) setErrors((p) => ({ ...p, tipoPago: undefined })); }} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white">
                                    <option value="">-- Seleccionar --</option>
                                    {tiposPago.map((tp, idx) => (
                                        <option key={idx} value={tp}>{tp}</option>
                                    ))}
                                </select>
                                <FieldError message={errors.tipoPago} />
                            </div>
                            <div className="space-y-1">
                                <label className="block text-xs font-medium text-gray-500">Precio:</label>
                                <input type="number" value={precio} onChange={e => { setPrecio(e.target.value); if (errors.precio) setErrors((p) => ({ ...p, precio: undefined })); }} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                                <FieldError message={errors.precio} />
                            </div>
                            <div className="space-y-1">
                                <label className="block text-xs font-medium text-gray-500">Duración (Hs):</label>
                                <input type="number" value={duracion} onChange={e => { setDuracion(e.target.value); if (errors.duracion) setErrors((p) => ({ ...p, duracion: undefined })); }} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                                <FieldError message={errors.duracion} />
                            </div>
                            <div className="space-y-1">
                                <label className="block text-xs font-medium text-gray-500">Cantidad Entradas:</label>
                                <input type="number" value={cantidadEntradas} onChange={e => { setCantidadEntradas(e.target.value); if (errors.cantidadEntradas) setErrors((p) => ({ ...p, cantidadEntradas: undefined })); }} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                                <FieldError message={errors.cantidadEntradas} />
                            </div>
                        </div>
                        <button onClick={handleCrear} className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors">Crear Entrada</button>
                    </div>
                )}

                <hr className="border-gray-200" />

                <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Entradas Existentes</h3>
                    {entradas.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo Pago</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duración (Hs)</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {entradas.map((entrada, idx) => {
                                        const editando = editandoId === entrada.id;
                                        return (
                                            <tr key={entrada.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm text-gray-700">
                                                    {editando ? (
                                                        <input type="text" value={entrada.nombreEntrada}
                                                            onChange={(e) => { const copy = [...entradas]; copy[idx].nombreEntrada = e.target.value; setEntradas(copy); }}
                                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                                                    ) : entrada.nombreEntrada}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700">
                                                    {editando ? (
                                                        <select value={entrada.tipoPago}
                                                            onChange={(e) => { const copy = [...entradas]; copy[idx].tipoPago = e.target.value; setEntradas(copy); }}
                                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                                                            <option value="">--</option>
                                                            {tiposPago.map((tp, i) => <option key={i} value={tp}>{tp}</option>)}
                                                        </select>
                                                    ) : entrada.tipoPago}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700">
                                                    {editando ? (
                                                        <input type="number" value={entrada.precio}
                                                            onChange={(e) => { const copy = [...entradas]; copy[idx].precio = e.target.value; setEntradas(copy); }}
                                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                                                    ) : entrada.precio}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700">
                                                    {editando ? (
                                                        <input type="number" value={entrada.duracion}
                                                            onChange={(e) => { const copy = [...entradas]; copy[idx].duracion = e.target.value; setEntradas(copy); }}
                                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                                                    ) : entrada.duracion}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700">
                                                    {editando ? (
                                                        <input type="number" value={entrada.cantidadEntradas}
                                                            onChange={(e) => { const copy = [...entradas]; copy[idx].cantidadEntradas = e.target.value; setEntradas(copy); }}
                                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                                                    ) : entrada.cantidadEntradas}
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    {editando ? (
                                                        <div className="flex gap-2">
                                                            <button onClick={() => handleUpdateEntrada(entrada)} className="px-3 py-1 bg-green-500 text-white text-xs font-medium rounded hover:bg-green-600 transition-colors">Guardar</button>
                                                            <button onClick={() => setEditandoId(null)} className="px-3 py-1 bg-gray-500 text-white text-xs font-medium rounded hover:bg-gray-600 transition-colors">Cancelar</button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex gap-2">
                                                            <button onClick={() => setEditandoId(entrada.id)} className="px-3 py-1 bg-primary-600 text-white text-xs font-medium rounded hover:bg-primary-700 transition-colors">Editar</button>
                                                            <button onClick={() => handleEliminarEntrada(entrada.id)} className="px-3 py-1 bg-danger-600 text-white text-xs font-medium rounded hover:bg-danger-700 transition-colors">Eliminar</button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">No hay entradas registradas.</p>
                    )}
                </div>

                <button onClick={onVolver} className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors">Volver</button>
            </div>
        </div>
    );
};

export default EntradaManager;
